import { useEffect, useState } from "react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from "@atgis/ui";
import { Check, HardDrive } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

// Teknik caps DB'den gelir; fiyat ve marketing copy burada tanımlı.
// Yeni bir tier DB'ye eklenince kart otomatik çıkar — marketing config opsiyoneldir.
type TierMarketing = {
  description: string;
  features: string[];
  cta: string;
  highlight?: boolean;
};

const TIER_MARKETING: Record<string, TierMarketing> = {
  free: {
    description: "Bireysel kullanıcılar ve serbest projeler için.",
    features: ["Sınırsız serbest proje", "Tekil kullanıcı çalışma alanı", "Topluluk desteği"],
    cta: "Ücretsiz Başla",
  },
  basic: {
    description: "Serbest projeleri paylaşmak isteyen küçük ekipler için.",
    features: [
      "Serbest proje paylaşımı (düzenleme / görüntüleme)",
      "E-posta desteği",
      "Coğrafi ve varlık tipi bazlı yetki",
    ],
    cta: "Plana Geç",
  },
  pro: {
    description: "Harita ve CBS ofisleri için organizasyon tabanlı ekip yönetimi.",
    features: [
      "Organizasyon + ekip üyesi daveti",
      "Sahadan ölçümleri ofisle eş zamanlı takip",
      "Serbest proje + ekip iş bölümü",
      "Öncelikli destek",
    ],
    cta: "Pro'ya Geç",
    highlight: true,
  },
  business: {
    description: "Görev yönetimi ve takım koordinasyonu isteyen kurumlar için.",
    features: [
      "Organizasyon + üye yönetimi",
      "Proje paylaşımı",
      "Görev panosu",
      "Öncelikli destek",
    ],
    cta: "Business'a Geç",
  },
  probusiness: {
    description: "Büyüyen organizasyonlar için tam ekip ve görev altyapısı.",
    features: [
      "Organizasyon + üye yönetimi",
      "Proje paylaşımı",
      "Görev panosu",
      "Ekip yönetimi",
      "Öncelikli destek",
    ],
    cta: "Pro Business'a Geç",
  },
  enterprise: {
    description: "Mevcut Altyapı, Altyapı Projesi, kurumsal veri modeli, sınırsız ekip.",
    features: [
      "Mevcut Altyapı, Altyapı Projesi, revizyon onay zinciri",
      "Veri modeli ve ekip yönetimi",
      "Görev panosu + özel öznitelik tanımı",
      "SLA, eğitim ve müşteri başarı uzmanı",
    ],
    cta: "İletişime Geç",
  },
};

type TierRow = {
  key: string;
  label: string;
  sort_order: number;
  storage_quota_mb: number;
  price_display: string;
  price_cadence: string | null;
  can_create_workspace: boolean;
  can_share: boolean;
  can_taskboard: boolean;
  can_teams: boolean;
  can_create_gis_workspace: boolean;
};

function formatStorage(mb: number): string | null {
  if (mb <= 0) return null;
  if (mb >= 1024) return `${mb / 1024} GB depolama`;
  return `${mb} MB depolama`;
}

export function Pricing() {
  const [tiers, setTiers] = useState<TierRow[]>([]);

  useEffect(() => {
    fetch("/api/tiers")
      .then((r) => r.json())
      .then(setTiers)
      .catch(() => {});
  }, []);

  // Yüklenmeden önce hardcoded fallback olarak TIER_MARKETING sırasını kullan
  const rows: TierRow[] =
    tiers.length > 0
      ? tiers
      : (Object.keys(TIER_MARKETING).map((key, i) => ({
          key,
          label: TIER_MARKETING[key]?.cta ?? key,
          sort_order: i,
          storage_quota_mb: 0,
          price_display: "—",
          price_cadence: null,
          can_create_workspace: false,
          can_share: false,
          can_taskboard: false,
          can_teams: false,
          can_create_gis_workspace: false,
        })) as TierRow[]);

  return (
    <section id="fiyat" className="border-b border-border/60">
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Planlar"
          title={
            <>
              Bireysel kullanıcıdan <span className="gradient-text">kuruma</span> kadar.
            </>
          }
          description="Tüm planlar aynı RADGIS deneyimini sunar; sadece açılan yetenek seti farklılaşır. Her kullanıcı sadece kendi yetkili olduğu menüleri ve butonları görür — yapamayacağı işler ekranında bile çıkmaz."
        />

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((tier) => {
            const marketing = TIER_MARKETING[tier.key];
            const storage = formatStorage(tier.storage_quota_mb);
            const features = marketing
              ? storage
                ? [...marketing.features, storage]
                : marketing.features
              : ([storage].filter(Boolean) as string[]);
            const highlight = marketing?.highlight ?? false;

            return (
              <Card
                key={tier.key}
                className={cn(
                  "relative flex h-full flex-col",
                  highlight && "border-primary/60 shadow-soft ring-1 ring-primary/30",
                )}
              >
                {highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    En popüler
                  </span>
                )}
                <CardHeader>
                  <CardTitle className="text-lg">{tier.label}</CardTitle>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-semibold tracking-tight">
                      {tier.price_display || "—"}
                    </span>
                    {tier.price_cadence && (
                      <span className="text-sm text-muted-foreground">{tier.price_cadence}</span>
                    )}
                  </div>
                  <CardDescription className="text-sm">
                    {marketing?.description ?? ""}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col">
                  <ul className="space-y-2 text-sm">
                    {features.map((f) => (
                      <li key={f} className="flex gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--project-master))]" />
                        <span className="text-foreground/80">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-2">
                    <Button className="w-full" variant={highlight ? "default" : "outline"} asChild>
                      <a href="#iletisim">{marketing?.cta ?? "Plana Geç"}</a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <StorageNote />

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Cloud planlarında self-service tier yükseltme aktiftir. KDV hariç fiyatlardır.
        </p>
      </div>
    </section>
  );
}

function StorageNote() {
  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-border/60 bg-card">
      <div className="flex flex-col gap-5 p-6 md:flex-row md:items-start md:gap-7 md:p-8">
        <div className="flex shrink-0 items-center gap-3 md:flex-col md:items-start md:gap-2">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <HardDrive className="h-5 w-5" />
          </div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Depolama nasıl çalışır?
          </span>
        </div>

        <div className="grid flex-1 gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-border/60 bg-background/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <span className="h-2 w-2 rounded-full bg-[hsl(var(--project-sub))]" />
              Pro · Workspace
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Workspace altındaki tüm projelere kim ek yüklerse yüklesin — kullanıcının planı Free,
              Basic, Pro fark etmez — dosyalar Pro hesabının{" "}
              <span className="font-medium text-foreground">10 GB</span> ortak workspace kotasından
              düşer. Ekibe maliyet bindirmezsiniz.
            </p>
          </div>

          <div className="rounded-xl border border-border/60 bg-background/60 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <span className="h-2 w-2 rounded-full bg-[hsl(var(--project-master))]" />
              Enterprise · Mevcut Altyapı & Altyapı Projesi
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Enterprise altındaki Mevcut Altyapı ve Altyapı Projesi projelerine eklenen tüm
              dosyalar — saha yüklenicisinin yüklediği fotoğraflar dahil — enterprise sahibinin{" "}
              <span className="font-medium text-foreground">500 GB</span> kurumsal kotasından düşer.
              Yüklenicilerin ek depolama ücreti ödemesine gerek yoktur.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-border/60 bg-primary/5 px-6 py-3 text-xs text-muted-foreground md:px-8">
        Her iki kotada da <span className="font-medium text-foreground">talebe göre artırma</span>{" "}
        mümkündür — büyüyen kurumsal arşiv için sınır yoktur.
      </div>
    </div>
  );
}
